import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '../use-api-backend-info';
import { ApiBackendInfo } from '../athena/types';
import { useState } from 'react';
import queryKeys from '@/constants/query-keys';
import { useQuery } from '@tanstack/react-query';
import { GetWinningNotifyResponse } from './types';
import { useAccountStatus } from '../account';
import { AccountActStatus } from '@nf/utils-common/constants';

const isMockEnabled = false;

/**
 * 取得 GoldenDuel 中獎通知
 * @param apiBackendInfo 
 * @returns ApiResponse<number> - GroupId (最早期數)
 */
const fetchGoldenDuelWinningNotify = async (apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<GetWinningNotifyResponse>> => {
	if (isMockEnabled) {
		return {
			Data: {
				GroupId: '1234567890'
			},
			Success: true,
			Message: '',
			ErrorCode: 0,
			ErrorMsg: ''
		};
	}
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/GoldenDuel/GetWinningNotify`)(apiBackendInfo.token);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/** 
 * 回寫 GoldenDuel 中獎已通知
 * @param apiBackendInfo 
 * @returns ApiResponse<void>
 */
const setGoldenDuelWinningNotify = async (apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<void>> => {
	if (isMockEnabled) {
		return {
			Data: undefined,
			Success: true,
			Message: '',
			ErrorCode: 0,
			ErrorMsg: ''
		};
	}
	const response = await fetchComposer.postWithBearer(`${apiBackendInfo.url}/GoldenDuel/SetWinningNotify`)(apiBackendInfo.token);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

export const useGoldenDuelWinningNotify = (enableGoldenDuel: boolean) => {
	const apiBackendInfo = useApiBackendInfo();
	const [hasCheckNotify, setHasCheckNotify] = useState(false);
	const { data: actStatus } = useAccountStatus();

	const query = useQuery({
		queryKey: [
			queryKeys.GOLDEN_DUEL_WINNING_NOTIFY,
			apiBackendInfo.url
		],
		queryFn: () => fetchGoldenDuelWinningNotify(apiBackendInfo),
		refetchOnWindowFocus: false,
		staleTime: Infinity,
		cacheTime: Infinity,
		retry: 5,
		retryDelay: 3000,
		enabled: enableGoldenDuel && actStatus === AccountActStatus.Deposited && apiBackendInfo.isReady && !hasCheckNotify,
		onSuccess: (response) => {
			if (response.Data) {
				setHasCheckNotify(true);
			}
		}
	});

	return {
		...query,
		setGoldenDuelWinningNotify: () => setGoldenDuelWinningNotify(apiBackendInfo)
	}
};
